// Full-size photo viewer.
//
// Opening a photo in a new tab dumps the reader on a bare CloudFront URL
// with no way back except the browser's own controls, and loses the set it
// belonged to. This keeps them on the page and lets them step through.
import React, { useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

interface Props {
  urls: string[];
  /** Which one is showing. Undefined closes the viewer. */
  index?: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  /** Describes the set, e.g. "Reference photo". */
  label?: string;
}

const ImageLightbox: React.FC<Props> = ({
  urls,
  index,
  onIndexChange,
  onClose,
  label = 'Photo',
}) => {
  const open = index !== undefined && index >= 0 && index < urls.length;

  const step = useCallback(
    (direction: 1 | -1) => {
      if (index === undefined) return;
      onIndexChange((index + direction + urls.length) % urls.length);
    },
    [index, onIndexChange, urls.length]
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    };
    window.addEventListener('keydown', onKey);
    // The page behind mustn't scroll while a full-screen overlay is up.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose, step]);

  if (!open || index === undefined) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${label} ${index + 1} of ${urls.length}`}
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
    >
      <img
        src={urls[index]}
        alt={`${label} ${index + 1}`}
        // Without this the backdrop's close handler fires on the photo.
        onClick={e => e.stopPropagation()}
        className="max-h-[90vh] max-w-[90vw] object-contain rounded shadow-2xl"
      />

      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
      >
        <X size={28} />
      </button>

      {urls.length > 1 && (
        <>
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              step(-1);
            }}
            aria-label="Previous photo"
            className="absolute left-2 sm:left-6 text-white/80 hover:text-white p-2"
          >
            <ChevronLeft size={36} />
          </button>
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              step(1);
            }}
            aria-label="Next photo"
            className="absolute right-2 sm:right-6 text-white/80 hover:text-white p-2"
          >
            <ChevronRight size={36} />
          </button>
          <span className="absolute bottom-4 text-white/70 text-sm">
            {index + 1} / {urls.length}
          </span>
        </>
      )}
    </div>
  );
};

export default ImageLightbox;
